import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import "./addDropModal.scss";
import Button, { ButtonSizes, ButtonThemes } from "ui-components/button/button";
import { getTerminalsList, terminalSummarySelector } from 'slices/terminal-summary/terminalSummarySlice';
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import { Form, Formik } from "formik";
import XGSFormSelect from "ui-components/form/select/xgsFormSelect";
import { useDispatch, useSelector } from "react-redux";
import { useLaneByPlanID } from "../hooks/useLanes";
import { staticLoadSelector } from "slices/static-load/staticLoadSlice";
import Loading from "ui-components/loading/loading";
import { useCreateDrop } from "../hooks/useDrop";
import { TerminalSummaryModel } from "app/data/terminal-summary/TerminalListModel";

// Bind modal to app root for accessibility
Modal.setAppElement("#root");

export interface Terminal {
    value: string;
    label: string;
}

export interface InsertPosition {
    value: string;
    label: string;
}

export interface AddDropModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    insertPositions?: InsertPosition[];
}

const normalizeAirportCode = (airportCode: string) => {
    return airportCode.replace(/[^a-zA-Z]/g, "");
}

const buildInsertPositionsFromTurnNumber = (
    turnNumber: string | undefined,
    terminals: { airportCode: string; terminalName: string }[] | undefined
): InsertPosition[] => {
    if (!turnNumber) return [];

    const segments = turnNumber.split("-");

    return segments.map((airportCode) => {
        const terminal = terminals?.find(
            (t) => t.airportCode.toLowerCase() === normalizeAirportCode(airportCode).toLowerCase()
        );
        const terminalLabel = terminal
            ? `${terminal.airportCode} – ${terminal.terminalName.toLowerCase().capitalize()}`
            : airportCode;

        return {
            value: `after_${airportCode.toLowerCase()}`,
            label: `After ${terminalLabel}`,
        };
    });
};

/**
 * Resolves insert position (e.g. after_bwi) to dropNumber.
 * Finds the selected stop in the drops array; inserts after it.
 * Example: Baltimore=1, Dallas=2 → after Baltimore → dropNumber 2 (Dallas shifts to 3).
 * After the last stop → undefined (API appends at the end).
 */
const resolveDropNumber = (
    insertPosition: string,
    drops: { terminalNumber?: string | number }[] | undefined,
    terminals: TerminalSummaryModel[] | undefined,
    turnNumber?: string
): number | undefined => {
    if (!insertPosition?.startsWith("after_") || !drops?.length) return undefined;

    const selectedCode = normalizeAirportCode(insertPosition.slice("after_".length)).toLowerCase();

    let index = drops.findIndex((drop) => {
        const terminal = terminals?.find(
            (t) => t.terminalNumber?.toString() === drop.terminalNumber?.toString()
        );
        return (
            !!terminal &&
            normalizeAirportCode(terminal.airportCode).toLowerCase() === selectedCode
        );
    });

    // Fallback: align with turnNumber segment order (same order as drops)
    if (index < 0 && turnNumber) {
        const segments = turnNumber.split("-").map((s) => normalizeAirportCode(s).toLowerCase());
        const segmentIndex = segments.findIndex((s) => s === selectedCode);
        if (segmentIndex >= 0 && segmentIndex < drops.length) {
            index = segmentIndex;
        }
    }

    if (index < 0) return undefined;
    // After last drop → omit dropNumber so API appends
    if (index === drops.length - 1) return undefined;

    return index + 2;
};

const AddDropModal: React.FC<AddDropModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    insertPositions: insertPositionsProp,
}) => {
    const dispatch = useDispatch();

    const staticLoadSelectorData = useSelector(staticLoadSelector);
    const selectedDivision = staticLoadSelectorData.selectedDivision;
    const weekStartDate = staticLoadSelectorData.weekStartDate;
    const selectedPlanId = staticLoadSelectorData.selectedPlanId;

    const { data: laneByPlanId, isLoading: isLoadingLaneByPlanID } = useLaneByPlanID(
        selectedPlanId, selectedDivision, weekStartDate)

    const [terminalOptions, setTerminalOptions] = useState<{
        value: string;
        label: string;
    }[]>([]);

    const terminalSummaryState = useSelector(terminalSummarySelector);
    const resetFormRef = useRef<(() => void) | null>(null);

    const handleModalClose = () => {
        resetFormRef.current?.();
        onClose();
    };

    const createDropMutation = useCreateDrop(() => {
        onSuccess?.();
        handleModalClose();
    });

    const insertPositions = useMemo(() => {
        if (insertPositionsProp) return insertPositionsProp;

        return buildInsertPositionsFromTurnNumber(
            laneByPlanId?.lane?.turnNumber,
            terminalSummaryState.terminallist
        );
    }, [
        insertPositionsProp,
        laneByPlanId?.lane?.turnNumber,
        terminalSummaryState.terminallist,
    ]);

    const defaultInsertPosition =
        insertPositions[insertPositions.length - 1]?.value ?? insertPositions[0]?.value ?? "";

    useEffect(() => {
        if (terminalSummaryState.terminallist) {
            const terminalOptions = terminalSummaryState.terminallist?.map((terminal) => ({
                value: terminal.terminalNumber.toString(),
                label: `${terminal.airportCode} - ${terminal.terminalName.toLowerCase().capitalize()} ${terminal.terminalNumber}`
            })) || [];
            setTerminalOptions(terminalOptions);
        }
    }, [terminalSummaryState.terminallist]);

    useEffect(() => {
        dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
        // eslint-disable-next-line
    }, []);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={handleModalClose}
            className="add-drop-modal"
            overlayClassName="add-drop-modal__overlay"
            closeTimeoutMS={200}
        >
            <Formik
                initialValues={{
                    terminalNumber: "",
                    insertPosition: defaultInsertPosition,
                }}
                enableReinitialize
                onSubmit={(values) => {
                    const terminal = terminalSummaryState.terminallist?.find(
                        (t) => t.terminalNumber.toString() === values.terminalNumber
                    );

                    if (!terminal || !selectedPlanId) return;

                    const dropNumber = resolveDropNumber(
                        values.insertPosition,
                        laneByPlanId?.drops,
                        terminalSummaryState.terminallist,
                        laneByPlanId?.lane?.turnNumber
                    );

                    createDropMutation.mutate({
                        planId: Number(selectedPlanId),
                        terminalNumber: Number(values.terminalNumber),
                        city: terminal.address?.city ?? "",
                        state: terminal.address?.stateProvince ?? "",
                        ...(dropNumber != null ? { dropNumber } : {}),
                    });
                }}
            >
                {(formikProps) => {
                    resetFormRef.current = formikProps.resetForm;

                    return (
                        <Form>
                            {/* Header */}
                            <div className="add-drop-modal__header">
                                <h2 className="add-drop-modal__title">Add Drop</h2>
                                <button
                                    type="button"
                                    className="add-drop-modal__close"
                                    onClick={handleModalClose}
                                    aria-label="Close modal"
                                >
                                    &times;
                                </button>
                            </div>
                            {/* Body */}
                            <div className="add-drop-modal__body">
                                <hr className="add-drop-modal__divider" />
                                {/* Terminal Field */}
                                <XGSFormSelect
                                    label="Terminal"
                                    name="terminalNumber"
                                    required={true}
                                    options={terminalOptions}
                                    controlled
                                    labelMode={LabelModes.column}
                                />
                                {/* Insert Position Field */}
                                <div className="add-drop-modal__field">
                                    <p className="add-drop-modal__label add-drop-modal__label--bold">
                                        Insert Position
                                    </p>
                                    {isLoadingLaneByPlanID ? (
                                        <div className="add-drop-modal__radio-group">
                                            <Loading isLoading={isLoadingLaneByPlanID} />
                                        </div>
                                    ) : (
                                    <div className="add-drop-modal__radio-group">
                                        {insertPositions.map((position, index) => (
                                            <div key={index}>
                                                <input
                                                    type="radio"
                                                    name="insertPosition"
                                                    value={position.value}
                                                    checked={formikProps.values.insertPosition === position.value}
                                                    onChange={() => formikProps.setFieldValue("insertPosition", position.value)}
                                                />
                                                <label htmlFor="insertPosition">{position.label}</label>
                                            </div>
                                        ))}
                                    </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="add-drop-modal__footer">
                                <Button type="button" theme={ButtonThemes.gray} size={ButtonSizes.auto} onClick={handleModalClose}>Cancel</Button>
                                <Button
                                    type="submit"
                                    theme={ButtonThemes.blue}
                                    size={ButtonSizes.auto}
                                    spinner={createDropMutation.isLoading}
                                    disabled={!formikProps.values.terminalNumber}
                                >
                                    Add Drop
                                </Button>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default AddDropModal;
